import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Paper, Tabs, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Button, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { levelConfigAPI, appConfigAPI } from '../services/api';
import { LevelConfig, AppConfigItem } from '../types';

const PointConfig: React.FC = () => {
  const [tab, setTab] = useState(0);
  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>포인트 / 직급 설정</Typography>
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="직급 설정" />
          <Tab label="앱 설정(광고/보상)" />
        </Tabs>
      </Paper>
      {tab === 0 ? <LevelConfigTab /> : <AppConfigTab />}
    </Box>
  );
};

// ===== 직급 설정 =====
const LevelConfigTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<LevelConfig | null>(null);
  const { register, handleSubmit, reset, setValue } = useForm<any>();

  const { data: levels } = useQuery({
    queryKey: ['level-configs'],
    queryFn: async () => (await levelConfigAPI.getAll()).data || [],
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['level-configs'] });

  const createMutation = useMutation({ mutationFn: levelConfigAPI.create, onSuccess: () => { invalidate(); handleClose(); } });
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<LevelConfig> }) => levelConfigAPI.update(id, data),
    onSuccess: () => { invalidate(); handleClose(); },
  });
  const deleteMutation = useMutation({ mutationFn: levelConfigAPI.delete, onSuccess: invalidate });

  const handleOpen = (lv?: LevelConfig) => {
    if (lv) {
      setEditing(lv);
      setValue('level', lv.level); setValue('name', lv.name); setValue('emoji', lv.emoji || '');
      setValue('expThreshold', lv.expThreshold); setValue('workReward', lv.workReward);
    }
    setOpen(true);
  };
  const handleClose = () => { setOpen(false); setEditing(null); reset(); };

  const onSubmit = (d: any) => {
    const data = {
      level: Number(d.level), name: d.name, emoji: d.emoji,
      expThreshold: Number(d.expThreshold), workReward: Number(d.workReward),
    };
    if (editing) updateMutation.mutate({ id: editing.id, data });
    else createMutation.mutate(data);
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>직급 추가</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>단계</TableCell><TableCell>이모지</TableCell><TableCell>직급명</TableCell>
              <TableCell align="right">필요 누적 EXP</TableCell><TableCell align="right">근무기록 수당</TableCell>
              <TableCell>상태</TableCell><TableCell align="center">작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {levels?.map((lv) => (
              <TableRow key={lv.id}>
                <TableCell>Lv.{lv.level}</TableCell>
                <TableCell>{lv.emoji}</TableCell>
                <TableCell>{lv.name}</TableCell>
                <TableCell align="right">{new Intl.NumberFormat('ko-KR').format(lv.expThreshold)}</TableCell>
                <TableCell align="right">{lv.workReward} P</TableCell>
                <TableCell><Chip label={lv.isActive ? '활성' : '비활성'} color={lv.isActive ? 'success' : 'default'} size="small" /></TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleOpen(lv)}><EditIcon /></IconButton>
                  <IconButton size="small" color="error" onClick={() => window.confirm('삭제하시겠습니까?') && deleteMutation.mutate(lv.id)}><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>{editing ? '직급 수정' : '직급 추가'}</DialogTitle>
          <DialogContent>
            <TextField margin="dense" label="단계(level)" type="number" fullWidth required disabled={!!editing} {...register('level', { required: true })} sx={{ mb: 2 }} />
            <TextField margin="dense" label="직급명" fullWidth required {...register('name', { required: true })} sx={{ mb: 2 }} />
            <TextField margin="dense" label="이모지" fullWidth {...register('emoji')} sx={{ mb: 2 }} />
            <TextField margin="dense" label="필요 누적 EXP" type="number" fullWidth required {...register('expThreshold', { required: true })} sx={{ mb: 2 }} />
            <TextField margin="dense" label="근무기록 수당(P)" type="number" fullWidth required {...register('workReward', { required: true })} />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>취소</Button>
            <Button type="submit" variant="contained">{editing ? '수정' : '추가'}</Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

// ===== 앱 설정 =====
// 임시 숨김 항목 (다시 노출하려면 아래 배열에서 해당 키 제거)
const HIDDEN_CONFIG_KEYS = [
  'gifticon_callback_no', // 기프티콘 발신번호(callback)
  'gifticon_mms_title', // 기프티콘 MMS 제목
  'gifticon_dev_yn', // Giftishow 개발모드(Y/N)
];

const AppConfigTab: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: configs } = useQuery({
    queryKey: ['app-configs'],
    queryFn: async () => (await appConfigAPI.getAll()).data || [],
  });

  const [values, setValues] = useState<Record<string, string>>({});
  useEffect(() => {
    if (configs) {
      const v: Record<string, string> = {};
      configs.forEach((c) => { v[c.key] = c.value; });
      setValues(v);
    }
  }, [configs]);

  const updateMutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) => appConfigAPI.update(key, value),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['app-configs'] }),
  });

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>설정 항목</TableCell><TableCell>키</TableCell><TableCell width={220}>값</TableCell><TableCell align="center">저장</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {configs?.filter((c: AppConfigItem) => !HIDDEN_CONFIG_KEYS.includes(c.key)).map((c: AppConfigItem) => (
            <TableRow key={c.key}>
              <TableCell>{c.description}</TableCell>
              <TableCell><Typography variant="caption" color="text.secondary">{c.key}</Typography></TableCell>
              <TableCell>
                <TextField
                  size="small" fullWidth
                  type={c.valueType === 'int' ? 'number' : 'text'}
                  value={values[c.key] ?? ''}
                  onChange={(e) => setValues((p) => ({ ...p, [c.key]: e.target.value }))}
                />
              </TableCell>
              <TableCell align="center">
                <Button size="small" variant="contained" onClick={() => updateMutation.mutate({ key: c.key, value: values[c.key] })}>저장</Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PointConfig;
